import { envValue, type EnvRecord } from "./envPreflight";

/**
 * TA-430, ruling C7 ("both", Kabir 2026-09-27): a production process never
 * drafts with MockLLMProvider. It refuses to boot instead, and says why.
 *
 * WHICH FLAG. `NODE_ENV=production`, the one the Dockerfile's runtime stage
 * already sets (`ENV NODE_ENV=production`) on every image this repo ships:
 * api, worker and migrate. So every ECS task in deploy/aws runs under it
 * without a new variable in any task definition, and nothing a developer runs
 * locally does unless they ask for it (vitest sets `test`, `pnpm dev` and the
 * e2e harness set nothing or `test`). A second, Ackra-specific production
 * switch would be a second answer to one question, and the one an operator
 * forgets to set is the one that decides.
 *
 * WHAT IT DOES NOT DO. It changes nothing outside production: the mock keeps
 * answering every local run and the mock eval exactly as it did, as the loud
 * degradation the preflight has always printed (section 0: loud to the
 * operator, never a refusal where a developer needs it to run).
 */
export const PRODUCTION_FLAG_ENV = "NODE_ENV";

/** True when this process runs under `NODE_ENV=production`, read through the one trimmed env read. */
export function isProductionEnv(env: EnvRecord): boolean {
  return envValue(env, PRODUCTION_FLAG_ENV) === "production";
}

/**
 * The preflight's `requiredWhen` reason for ANTHROPIC_API_KEY: null outside
 * production, so the spec keeps its degradation there. Worded to be followed
 * by the preflight's own ". Without it, <missingMeans>", so it ends without a
 * full stop.
 */
export function productionMockReason(env: EnvRecord): string | null {
  if (!isProductionEnv(env)) return null;
  return (
    `${PRODUCTION_FLAG_ENV}=production is set, and ruling C7 (TA-430) forbids a production ` +
    "process from drafting with MockLLMProvider: an owner reading a mock's draft as a real " +
    "model's answer is the silent degradation section 0 names. Set the key, or run under a " +
    `${PRODUCTION_FLAG_ENV} other than production for development and the mock eval`
  );
}

/**
 * The backstop's refusal block, printed when the runtime a process actually
 * built composes with the mock under production although its preflight
 * passed. House style: the process name, then indented lines that say what
 * was found, why it refuses, and the two ways out.
 */
export function productionMockRefusal(processName: string): string[] {
  return [
    `${processName} refused to start: its model provider resolved to MockLLMProvider under ${PRODUCTION_FLAG_ENV}=production.`,
    "  Ruling C7 (TA-430): a production process never drafts with the mock. Every draft it",
    "  wrote would read, to the owner approving it, like a real model's answer.",
    "  Fix: give this process ANTHROPIC_API_KEY (in deploy/aws the worker receives it only",
    "  when enable_model_calls = true), or, for development and the mock eval, run it under",
    `  a ${PRODUCTION_FLAG_ENV} other than production.`,
  ];
}

/**
 * TA-1277 checkpointer round (Kabir's typed go of 2026-10-12). The env that
 * gives a process outside production the production branch's checkpoint
 * start: check that migrate made the LangGraph tables, never create them
 * (packages/agents/src/graph/checkpointer.ts). Its one accepted value is
 * `CHECKPOINT_TABLES_MIGRATED`; any other non-empty value is a refusal.
 *
 * It lives here, beside the production flag, because the branch it selects
 * is that flag's, and because `pnpm demo` (apps/web) sets it for itself and
 * the worker it spawns, and reads env names from this package. It moves
 * nothing else: the preflight and the model path still read NODE_ENV only.
 */
export const CHECKPOINT_TABLES_ENV = "ACKRA_CHECKPOINT_TABLES";
export const CHECKPOINT_TABLES_MIGRATED = "migrated";
